import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatCurrency, formatDate, formatVal } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { PageHeader } from "./Dashboard";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Truck, Container, CheckCircle2, XCircle, UserCheck, UserX, Clock,
  Search, Eye, RefreshCw, FlaskConical, Layers, Send, AlertTriangle,
} from "lucide-react";
import { AlvysNotesButton } from "../components/AlvysNotes";
import { RentflowRepushDialog } from "../components/RentflowRepushDialog";

const FILTERS = [
  { key: "all", label: "All", icon: Layers, tint: "text-slate-600", match: () => true },
  { key: "unit_matched", label: "Unit Matched", icon: CheckCircle2, tint: "text-emerald-600", match: (r) => r.unit_flag === "matched", count: "unit_matched" },
  { key: "unit_unmatched", label: "Unit Not Matched", icon: XCircle, tint: "text-red-600", match: (r) => r.unit_flag === "unmatched", count: "unit_unmatched" },
  { key: "rental_matched", label: "Renter Identified", icon: UserCheck, tint: "text-sky-600", match: (r) => r.rental_flag === "matched", count: "rental_matched", rental: true },
  { key: "rental_unmatched", label: "No Renter Found", icon: UserX, tint: "text-amber-600", match: (r) => r.rental_flag === "unmatched", count: "rental_unmatched", rental: true },
  { key: "push_pushed", label: "Pushed to RentFlow", icon: Send, tint: "text-emerald-600", match: (r) => r.push_flag === "pushed", count: "push_pushed", push: true },
  { key: "push_failed", label: "Push Failed", icon: AlertTriangle, tint: "text-amber-600", match: (r) => r.push_flag === "error", count: "push_failed", push: true },
  { key: "pending", label: "Pending Lookup", icon: Clock, tint: "text-indigo-600", match: (r) => r.unit_flag === "pending" || r.rental_flag === "pending", count: "pending" },
];

const GRID_COLS = { 4: "lg:grid-cols-4", 6: "lg:grid-cols-6", 7: "lg:grid-cols-7", 8: "lg:grid-cols-8" };

const unitStatusClass = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("inactive")) return "bg-slate-100 text-slate-600 border-slate-200";
  if (s.includes("active") || s.includes("on rent")) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (s.includes("available")) return "bg-sky-50 text-sky-700 border-sky-200";
  if (s.includes("service")) return "bg-indigo-50 text-indigo-700 border-indigo-200";
  if (s.includes("maintenance")) return "bg-amber-50 text-amber-800 border-amber-200";
  if (s.includes("out")) return "bg-red-50 text-red-700 border-red-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
};

const Flag = ({ ok, pending, yes, no }) => {
  if (pending) return <span className="inline-flex items-center gap-1 text-xs text-indigo-600"><Clock className="h-3.5 w-3.5" /> Pending</span>;
  return ok
    ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600">{yes}</span>
    : <span className="inline-flex items-center gap-1 text-xs text-red-600">{no}</span>;
};

export default function Fleet() {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [pushingId, setPushingId] = useState(null);
  const [repushRow, setRepushRow] = useState(null);
  const navigate = useNavigate();
  const { isWriter } = useAuth();

  const load = useCallback(async () => {
    try {
      const res = await api.fleetBoard();
      setBoard(res.data);
    } catch (e) {
      console.error("Fleet: failed to load board", e);
      toast.error("Could not load the fleet board");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resyncAll = async () => {
    setSyncing(true);
    try {
      const res = await api.fleetEnrichAll();
      toast.success(`Re-synced ${res.data.enriched} bill(s) with Alvys & RentFlow`);
      await load();
    } catch {
      toast.error("Re-sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const retryFailedPushes = async () => {
    setRetrying(true);
    try {
      const res = await api.retryFailedRentflow();
      const { attempted, succeeded, failed } = res.data || {};
      if (!attempted) {
        toast.info("No failed pushes to retry.");
      } else if (failed) {
        toast.warning(`Retried ${attempted}: ${succeeded} succeeded, ${failed} still failing.`);
      } else {
        toast.success(`Retried ${attempted} failed push(es) — all succeeded.`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.status === 503 ? "RentFlow push is not configured." : "Retry failed");
    } finally {
      setRetrying(false);
    }
  };

  const doPush = async (row, force = false) => {
    if (pushingId) return;
    setPushingId(row.id);
    try {
      await api.pushRentflow(row.id, { force });
      toast.success(force ? "Re-pushed to RentFlow" : "Pushed to RentFlow");
      await load();
    } catch (e) {
      toast.error("RentFlow push failed", { description: e?.message });
    } finally {
      setPushingId(null);
    }
  };

  const pushRow = (row) => {
    if (row.push_flag === "pushed") setRepushRow(row);
    else doPush(row, false);
  };

  const counts = board?.counts || {};
  const alvysLive = board?.alvys_mode === "live";
  const rentflowLive = board?.rentflow_mode === "live";
  const rentflowEnabled = board?.rentflow_enabled !== false && (board?.rentflow_mode === "live" || board?.rentflow_mode === "mock");
  const pushEnabled = !!board?.rentflow_push_enabled;
  const showBanner = board && (!alvysLive || !rentflowEnabled);
  const bannerText = !alvysLive && !rentflowEnabled
    ? "Demo data. Connect the Alvys API for live units; renter lookups (RentFlow) are turned off."
    : !rentflowEnabled
      ? "Unit data is live from Alvys. Renter lookups are turned off \u2014 add RentFlow API credentials to enable renter data."
      : !rentflowLive
        ? "Unit data is live from Alvys. Renter data is demo \u2014 connect the RentFlow API for live renters."
        : "Renter data is live from RentFlow. Unit data is demo \u2014 connect the Alvys API for live units.";

  const visibleFilters = FILTERS.filter((f) => (rentflowEnabled || !f.rental) && (pushEnabled || !f.push));
  const activeFilter = visibleFilters.find((f) => f.key === filter) || visibleFilters[0];
  const gridColsClass = GRID_COLS[visibleFilters.length] || "lg:grid-cols-6";

  const rows = (board?.rows || [])
    .filter(activeFilter.match)
    .filter((r) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return [r.issuing_authority, r.license_plate, r.unit_number, r.owner_name, ...(r.renters || [])]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fleet & Rental"
        subtitle={rentflowEnabled ? "Audit how bills matched to Alvys units and RentFlow renters" : "Audit how bills matched to Alvys units"}
        right={
          isWriter && (
            <div className="flex items-center gap-2">
              {pushEnabled && (counts.push_failed || 0) > 0 && (
                <Button variant="outline" onClick={retryFailedPushes} disabled={retrying} className="gap-2" data-testid="rentflow-retry-failed-button">
                  <Send className={`h-4 w-4 ${retrying ? "animate-pulse" : ""}`} /> {retrying ? "Retrying..." : `Retry failed pushes (${counts.push_failed})`}
                </Button>
              )}
              <Button onClick={resyncAll} disabled={syncing} className="gap-2" data-testid="fleet-resync-all-button">
                <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Re-syncing..." : "Re-sync all"}
              </Button>
            </div>
          )
        }
      />

      {showBanner && (
        <div data-testid="fleet-demo-banner" className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <FlaskConical className="h-4 w-4 shrink-0" />
          <span>{bannerText}</span>
        </div>
      )}

      {/* Filter summary cards */}
      <div className={`grid grid-cols-2 gap-3 ${gridColsClass}`}>
        {visibleFilters.map(({ key, label, icon: Icon, tint, count }) => (
          <Card
            key={key}
            data-testid={`fleet-filter-${key}`}
            className={`cursor-pointer transition-[box-shadow,border-color] duration-200 hover:shadow-sm ${filter === key ? "border-[hsl(var(--ring))] shadow-sm" : ""}`}
            onClick={() => setFilter(key)}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide leading-tight">{label}</span>
                <Icon className={`h-4 w-4 ${tint}`} />
              </div>
              <div className="mt-2 text-2xl font-semibold font-heading tabular-nums">
                {key === "all" ? (counts.total ?? 0) : (counts[count] ?? 0)}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="fleet-search-input" placeholder="Search authority, plate, unit #, owner, renter..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">{[1,2,3,4].map(i => <Skeleton key={i} className="h-12 rounded-lg" />)}</div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent mb-3 mx-auto"><Truck className="h-7 w-7 text-primary" /></div>
              <h3 className="font-heading font-semibold">No bills in “{activeFilter.label}”</h3>
              <p className="text-sm text-muted-foreground mt-1">Upload toll bills, or adjust the filter.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="fleet-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Authority / Plate</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Unit match</TableHead>
                    {rentflowEnabled && <TableHead>Renter(s)</TableHead>}
                    {rentflowEnabled && <TableHead>Renter match</TableHead>}
                    {pushEnabled && <TableHead>RentFlow</TableHead>}
                    <TableHead className="text-right">Total due</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const UnitIcon = (r.unit_type || "").toLowerCase().includes("truck") ? Truck : Container;
                    return (
                    <TableRow key={r.id} data-testid="fleet-row" className="transition-colors hover:bg-[hsl(210_25%_96%)] cursor-pointer" onClick={() => navigate(`/documents/${r.document_id}`)}>
                      <TableCell>
                        <div className="font-medium text-sm">{formatVal(r.issuing_authority)}</div>
                        <div className="text-xs text-muted-foreground font-mono">{formatVal(r.license_plate)} {r.plate_state ? `(${r.plate_state})` : ""}</div>
                      </TableCell>
                      <TableCell>
                        {r.unit_number ? (
                          <div>
                            <div className="text-sm font-mono font-medium flex items-center gap-1.5"><UnitIcon className="h-3.5 w-3.5 text-muted-foreground" />{r.unit_number}</div>
                            {r.unit_status && <span className={`inline-flex mt-0.5 rounded-full border px-1.5 py-0.5 text-[11px] font-medium ${unitStatusClass(r.unit_status)}`}>{r.unit_status}</span>}
                          </div>
                        ) : <span className="text-sm text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        <Flag ok={r.unit_flag === "matched"} pending={r.unit_flag === "pending"}
                          yes={<><CheckCircle2 className="h-3.5 w-3.5" /> Matched</>}
                          no={<><XCircle className="h-3.5 w-3.5" /> No match</>} />
                      </TableCell>
                      {rentflowEnabled && (
                      <TableCell>
                        {(r.renters || []).length > 0 ? (
                          <div className="text-sm">
                            {r.renters.slice(0, 2).join(", ")}{r.renters.length > 2 ? ` +${r.renters.length - 2}` : ""}
                            <div className="text-xs text-muted-foreground">{r.rental_count} date(s)</div>
                          </div>
                        ) : <span className="text-sm text-muted-foreground">—</span>}
                      </TableCell>
                      )}
                      {rentflowEnabled && (
                      <TableCell>
                        <Flag ok={r.rental_flag === "matched"} pending={r.rental_flag === "pending"}
                          yes={<><UserCheck className="h-3.5 w-3.5" /> Identified</>}
                          no={<><UserX className="h-3.5 w-3.5" /> None</>} />
                      </TableCell>
                      )}
                      {pushEnabled && (
                      <TableCell data-testid="fleet-push-cell">
                        {r.push_flag === "pushed" ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600" title={r.pushed_at ? `Pushed ${formatDate(r.pushed_at)}` : "Pushed to RentFlow"}>
                            <CheckCircle2 className="h-3.5 w-3.5" /> Pushed
                          </span>
                        ) : r.push_flag === "error" ? (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-600" title={r.push_error || "Push failed"}>
                            <AlertTriangle className="h-3.5 w-3.5" /> Failed
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      )}
                      <TableCell className="text-right text-sm font-semibold tabular-nums">{formatCurrency(r.total_amount_due, r.currency)}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-0.5">
                          {pushEnabled && isWriter && (r.rental_flag === "matched" || r.push_flag !== "none") && (
                            <Button
                              size="icon"
                              variant="ghost"
                              data-testid="fleet-row-push"
                              disabled={pushingId === r.id}
                              title={r.push_flag === "pushed" ? "Re-push to RentFlow" : r.push_flag === "error" ? "Retry RentFlow push" : "Push to RentFlow"}
                              onClick={() => pushRow(r)}
                            >
                              <Send className={`h-4 w-4 ${pushingId === r.id ? "animate-pulse" : ""} ${r.push_flag === "error" ? "text-amber-600" : r.push_flag === "pushed" ? "text-emerald-600" : ""}`} />
                            </Button>
                          )}
                          <AlvysNotesButton notes={r.unit_notes} unitNumber={r.unit_number} variant="icon" testid="fleet-row-notes" />
                          <Button size="icon" variant="ghost" data-testid="fleet-row-view" onClick={() => navigate(`/documents/${r.document_id}`)}><Eye className="h-4 w-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );})}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      <RentflowRepushDialog
        open={!!repushRow}
        onOpenChange={(o) => !o && setRepushRow(null)}
        push={repushRow ? { pushed_at: repushRow.pushed_at } : null}
        onConfirm={() => { const r = repushRow; setRepushRow(null); if (r) doPush(r, true); }}
      />
    </div>
  );
}
